import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import AuthLayout, { Field, inputStyle } from './AuthLayout';
import { useZh, useOpenSignup } from '../lib/useProduct';

const makeSchema = (zh: boolean) => z.object({
  email: z.string().email(zh ? '请输入有效的邮箱' : 'Enter a valid email'),
  password: z.string().min(1, zh ? '请输入密码' : 'Password required'),
});
type FormVals = z.infer<ReturnType<typeof makeSchema>>;

export default function Login() {
  const zh = useZh();
  const openSignup = useOpenSignup();
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const inviteFromUrl = params.get('invite') || '';
  // Honor `?next=/some/path` so deep links like /admin/users that bounced
  // through /login bring the user back where they intended. Restricted to
  // same-origin in-app paths to prevent open-redirect abuse.
  const rawNext = params.get('next') || '';
  const nextPath = rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : '/';
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormVals>({
    resolver: zodResolver(makeSchema(zh)),
  });

  async function onSubmit(values: FormVals) {
    setSubmitErr(null);
    const res = await fetch('/api/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(values),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      setSubmitErr(body.message || (zh ? '登录失败，请检查邮箱和密码。' : 'Sign in failed.'));
      return;
    }
    // The session query was cached while anonymous (staleTime 5min), and
    // every signed-in check reads it — navigating on the stale cache renders
    // the logged-out landing page, so a successful login LOOKS like a bounce
    // back to the homepage. Refetch everything before moving. (Logout has
    // always done the equivalent with a hard navigation.)
    await qc.invalidateQueries();
    navigate(nextPath);
  }

  return (
    <AuthLayout
      title={zh ? '登录' : 'Sign in'}
      subtitle={zh ? '欢迎回来' : 'Welcome back.'}
      footer={
        <>
          {inviteFromUrl ? (
            <>{zh ? '有邀请码？' : 'Have an invite? '}<Link to={`/register?invite=${encodeURIComponent(inviteFromUrl)}`} style={{ color: 'var(--pri)' }}>{zh ? '创建账号' : 'Create an account'}</Link><br /></>
          ) : openSignup ? (
            /* Open signup — point people at /register instead of telling them
               to go ask for an invite that isn't needed. */
            <>{zh ? '还没有账号？' : "Don't have an account? "}<Link to="/register" style={{ color: 'var(--pri)' }}>{zh ? '免费注册' : 'Sign up free'}</Link><br /></>
          ) : (
            <>{zh ? '还没有账号？请向管理员索取邀请链接。' : "Don't have an account? Ask the maintainer for an invite link."}<br /></>
          )}
          <Link to="/forgot-password" style={{ color: 'var(--pri)' }}>{zh ? '忘记密码？' : 'Forgot password?'}</Link>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Field label={zh ? '邮箱' : 'Email'} error={errors.email?.message}>
          <input type="email" autoFocus autoComplete="email" {...register('email')} style={inputStyle} />
        </Field>
        <Field label={zh ? '密码' : 'Password'} error={errors.password?.message}>
          <input type="password" autoComplete="current-password" {...register('password')} style={inputStyle} />
        </Field>
        {submitErr && (
          <div style={{ padding: 10, background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 6, fontSize: 13, marginBottom: 14, color: '#991b1b' }}>{submitErr}</div>
        )}
        <button type="submit" className="btn accent" style={{ width: '100%' }} disabled={isSubmitting}>
          {isSubmitting ? (zh ? '登录中…' : 'Signing in…') : (zh ? '登录' : 'Sign in')}
        </button>
      </form>
    </AuthLayout>
  );
}
